/**
 * Base compartilhada do formulário público de Percepção de Riscos (IMGR).
 * Junta os DADOS REAIS do sistema (`IMGR_FORM`, `CARGOS`) ao design aprovado
 * (ícones, cores e textos de abertura por bloco). As duas cascas — desktop
 * (blocos por tela) e mobile (estilo Typeform) — consomem este módulo.
 */
import { CARGOS, IMGR_FORM } from "@caint/data";
import type { Cargo, ImgrEscalaItem, ImgrRespostaDTO } from "@caint/shared";

/** Resultado do envio das respostas ao servidor. */
export type ResultadoEnvio = "ok" | "fechado" | { erro: string };
/** Função de envio injetada pela casca orquestradora (carrega meta + estados). */
export type Enviar = (dto: ImgrRespostaDTO) => Promise<ResultadoEnvio>;

/** Traços SVG (24×24) usados nos ícones do formulário. */
export const ICONES: Record<string, string> = {
  conhecimento: "M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z",
  percepcao:
    "M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h16.9a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0ZM12 9v4M12 17h.01",
  controles:
    "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1V21a2 2 0 0 1-4 0v-.1A1.6 1.6 0 0 0 7 19.4a1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0-1.1-2.7H1a2 2 0 0 1 0-4h.1A1.6 1.6 0 0 0 2.6 7a1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H7a1.6 1.6 0 0 0 1-1.5V1a2 2 0 0 1 4 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V7a1.6 1.6 0 0 0 1.5 1H23a2 2 0 0 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1Z",
  comunicacao: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z",
  aprendizado: "M22 10v6M2 10l10-5 10 5-10 5zM6 12v5c3 3 9 3 12 0v-5",
  seguranca: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10ZM9 12l2 2 4-4",
  clock: "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 6v6l4 2",
  lock: "M19 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2ZM7 11V7a5 5 0 0 1 10 0v4",
  heart: "M12 21s-7-4.5-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 11c0 5.5-7 10-7 10Z",
  user: "M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z",
  check: "M20 6 9 17l-5-5",
  arrow: "M5 12h14M13 6l6 6-6 6",
  back: "M19 12H5M11 6l-6 6 6 6",
  backsm: "M15 18l-6-6 6-6",
};

export function Icone({ nome, s = 22, w = 2, c }: { nome: string; s?: number; w?: number; c?: string }) {
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      stroke={c ?? "currentColor"}
      strokeWidth={w}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={ICONES[nome]} />
    </svg>
  );
}

/** Marca CAINT (escudo + barras). Lê os tokens da paleta em runtime. */
export function Marca({ size = 30 }: { size?: number }) {
  const rs = typeof window !== "undefined" ? getComputedStyle(document.documentElement) : null;
  const teal = rs?.getPropertyValue("--teal-600").trim() || "#585FA0";
  const coral = rs?.getPropertyValue("--coral-500").trim() || "#D06B7C";
  const bars: [number, number][] = [
    [13.6, 6.5],
    [19.1, 9.5],
    [24.6, 12.5],
    [30.1, 15.5],
  ];
  const ops = [0.55, 0.72, 0.86, 1];
  const [lx, lh] = bars[bars.length - 1] ?? [30.1, 15.5];
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" aria-hidden="true">
      <path d="M10 8 Q10 6 12 6 L36 6 Q38 6 38 8 L38 26 Q38 34.5 24 42 Q10 34.5 10 26 Z" fill={teal} />
      {bars.map((b, i) => (
        <rect
          key={b[0]}
          x={b[0]}
          y={31 - b[1]}
          width="4.4"
          height={b[1]}
          rx="1.4"
          fill="#fff"
          opacity={ops[i]}
        />
      ))}
      <circle cx={lx + 2.2} cy={31 - lh} r="2.5" fill={coral} stroke={teal} strokeWidth="0.8" />
    </svg>
  );
}

/** Pergunta numerada (numeração global 1..31, igual à chave do envio). */
export interface PerguntaUi {
  readonly num: number;
  readonly texto: string;
}
/** Bloco/seção com os metadados visuais do design ligados ao componente real. */
export interface SecaoUi {
  readonly id: string; // C1..C6 (do sistema)
  readonly nome: string;
  readonly icone: string;
  readonly cor: string;
  readonly intro: string;
  readonly perguntas: readonly PerguntaUi[];
}

/** Ícone, cor e abertura de cada bloco, na ordem dos componentes do sistema. */
const META_BLOCOS: { icone: string; cor: string; intro: string }[] = [
  {
    icone: "conhecimento",
    cor: "var(--teal-600)",
    intro: "O quanto a equipe conhece as atividades críticas e os fatores que afetam o serviço.",
  },
  {
    icone: "percepcao",
    cor: "var(--coral-500)",
    intro: "A clareza da unidade sobre seus problemas, vulnerabilidades e como antecipá-los.",
  },
  {
    icone: "controles",
    cor: "#2D74B8",
    intro: "As rotinas e mecanismos que a unidade usa para evitar erros e interrupções.",
  },
  {
    icone: "comunicacao",
    cor: "#7B4FB0",
    intro: "Como as falhas e informações circulam entre a equipe e a gestão.",
  },
  {
    icone: "aprendizado",
    cor: "var(--teal-500)",
    intro: "O quanto a unidade aprende com os problemas e melhora de forma contínua.",
  },
  {
    icone: "seguranca",
    cor: "var(--coral-600)",
    intro: "As práticas e a prioridade dada à segurança do paciente na unidade.",
  },
];

/** Seções reais (IMGR_FORM) já enriquecidas com o design. */
export const SECOES: readonly SecaoUi[] = IMGR_FORM.componentes.map((c, i) => ({
  id: c.id,
  nome: c.nome,
  icone: META_BLOCOS[i]?.icone ?? "conhecimento",
  cor: META_BLOCOS[i]?.cor ?? "var(--primary)",
  intro: META_BLOCOS[i]?.intro ?? "",
  perguntas: c.questoes.map((q) => ({ num: q.num, texto: q.texto })),
}));

export const TOTAL = SECOES.reduce((n, s) => n + s.perguntas.length, 0);

/** Escala de concordância do mais positivo ao mais negativo (3→0), como no design. */
export const ESCALA_TOPO: readonly ImgrEscalaItem[] = [...IMGR_FORM.escala].sort((a, b) => b.valor - a.valor);

/** Cor da opção pela nota (curva de maturidade). */
export function corEscala(valor: number): string {
  if (valor >= 3) return "var(--mat-4)";
  if (valor === 2) return "var(--mat-3)";
  if (valor === 1) return "var(--mat-2)";
  return "var(--mat-0)";
}

/** Cargos agrupados por área (para o seletor de identificação). `idx` indexa a
 * MESMA lista passada (cargos da unidade, da matriz, ou o catálogo global). */
export function cargosPorArea(cargos: readonly Cargo[] = CARGOS): [string, { c: Cargo; idx: number }[]][] {
  const map = new Map<string, { c: Cargo; idx: number }[]>();
  cargos.forEach((c, idx) => {
    const arr = map.get(c.area) ?? [];
    arr.push({ c, idx });
    map.set(c.area, arr);
  });
  return [...map.entries()];
}
